import { OrbindApiError } from '@orbind/api-client'
import { Alert, Button, Card, Checkbox, FieldBlock, Input } from '@orbind/ui'
import { useQueryClient } from '@tanstack/react-query'
import { useState, type FormEvent } from 'react'
import { Navigate, useLocation, useNavigate } from 'react-router'
import { api, getStoredLocale, setStoredLocale } from '../lib/api.ts'
import { OrbindLogo } from '../shell/OrbindLogo.tsx'
import './login.css'

/** Escolha do "Manter conectado" lembrada neste aparelho (SPEC-20261022-1004-manter-conectado-30-dias). */
const CHAVE_MANTER = 'orbind.manter-conectado'

/**
 * AUTH-01 — Login (spec 10.1).
 *
 * Desktop em duas colunas (marca a esquerda, formulario a direita); mobile em
 * coluna unica. O seletor de idioma fica visivel ANTES do login, porque a
 * mensagem de erro precisa sair no idioma de quem esta tentando entrar.
 */
export function LoginPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  /*
    "Manter conectado" (SPEC-20261022-1004-manter-conectado-30-dias): marcado, a sessão dura
    30 dias e renova a cada uso; sem marcar, 24 horas. Antes era "Lembrar
    acesso", que não fazia nada. A escolha fica lembrada neste aparelho.
  */
  const [manterConectado, setManterConectado] = useState(() => {
    try {
      return localStorage.getItem(CHAVE_MANTER) === '1'
    } catch {
      return false
    }
  })
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [locale, setLocale] = useState(getStoredLocale())
  // cadastro do onboarding pendente: oferecemos reenviar o link (SPEC-20260814-1922)
  const [emailPendente, setEmailPendente] = useState<string | null>(null)
  const [reenviado, setReenviado] = useState(false)

  const queryClient = useQueryClient()

  if (api.isAuthenticated) return <Navigate to="/home" replace />

  async function onSubmit(event: FormEvent) {
    event.preventDefault()
    setLoading(true)
    setError(null)
    try {
      try {
        localStorage.setItem(CHAVE_MANTER, manterConectado ? '1' : '0')
      } catch {
        // sem localStorage: só não lembra a escolha
      }
      const dados = await api.login(email, password, manterConectado)
      // nada do que estava em cache pertence a quem acabou de entrar: outra
      // pessoa pode ter usado esta aba antes (dados, sessão, canais de acesso)
      queryClient.clear()
      // conta sem nenhum workspace: cai na tela de criar o primeiro
      // (SPEC-20260814-2128); as credenciais vao por state, so em memoria
      if ('needsWorkspace' in dados && dados.needsWorkspace) {
        navigate('/setup-workspace', { state: { email, password } })
        return
      }
      const from = (location.state as { from?: string } | null)?.from
      navigate(from ?? '/home', { replace: true })
    } catch (err) {
      // A senha e limpa, o e-mail permanece preenchido (spec 10.1).
      setPassword('')
      if (err instanceof OrbindApiError && err.code === 'EMAIL_NOT_CONFIRMED') {
        setEmailPendente(email)
        setReenviado(false)
        setError(null)
      } else {
        setEmailPendente(null)
        setError(
          err instanceof OrbindApiError && err.status === 401
            ? 'E-mail ou senha incorretos. Verifique e tente novamente.'
            : 'Não foi possível entrar agora. Tente novamente em instantes.',
        )
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="ob-root login">
      <section className="login__brand" aria-hidden>
        <div className="login__logo">
          <OrbindLogo size={44} color="#FFFFFF" halo="var(--ob-primary)" />
        </div>
        <h1 className="login__brand-title">Orbind</h1>
        <p className="login__brand-text">
          Um pedido entra, vira Itens de Trabalho, aparece no Kanban certo e alimenta as métricas. Tudo rastreável,
          do pedido à entrega.
        </p>
        <div className="login__chips">
          <span>Portfólio</span>
          <span>Itens de Trabalho</span>
          <span>Kanban</span>
          <span>Automações</span>
        </div>
      </section>

      <section className="login__panel">
        <div className="login__locale">
          <button
            type="button"
            onClick={() => {
              const next = locale === 'pt-BR' ? 'en-US' : 'pt-BR'
              setLocale(next)
              setStoredLocale(next)
            }}
          >
            <i className="fas fa-globe" aria-hidden /> {locale}
          </button>
        </div>

        <Card className="login__card">
          <h2 className="login__title">Entrar na plataforma</h2>
          <p className="login__subtitle">Use as credenciais da sua conta Orbind.</p>

          <form className="ob-stack" onSubmit={onSubmit} noValidate>
            {error ? (
              <Alert tone="danger" title="Não foi possível entrar">
                {error}
              </Alert>
            ) : null}
            {emailPendente ? (
              <Alert
                tone="warning"
                title="Confirme seu e-mail para entrar"
                action={
                  <Button
                    variant="secondary"
                    icon="fas fa-paper-plane"
                    onClick={() => {
                      void api.onboarding['resend-confirmation'].$post({ json: { email: emailPendente } })
                      setReenviado(true)
                    }}
                  >
                    {reenviado ? 'Link reenviado' : 'Reenviar link'}
                  </Button>
                }
              >
                Enviamos um link de confirmação para {emailPendente} no cadastro. Clique nele para ativar a conta.
              </Alert>
            ) : null}

            <FieldBlock label="E-mail" required>
              {({ id, invalid }) => (
                <Input
                  id={id}
                  type="email"
                  autoComplete="email"
                  autoFocus
                  required
                  invalid={invalid}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="voce@empresa.com.br"
                />
              )}
            </FieldBlock>

            <FieldBlock label="Senha" required>
              {({ id, invalid }) => (
                <Input
                  id={id}
                  type="password"
                  autoComplete="current-password"
                  required
                  invalid={invalid}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              )}
            </FieldBlock>

            <div className="login__row">
              <Checkbox
                label="Manter conectado"
                title="Fica conectado por 30 dias, renovados a cada uso. Sem marcar, a sessão dura 24 horas."
                checked={manterConectado}
                onChange={(e) => setManterConectado(e.target.checked)}
              />
              <a href="/forgot-password" className="login__link">
                Esqueci minha senha
              </a>
            </div>

            <Button type="submit" variant="primary" loading={loading}>
              Entrar
            </Button>

            <p style={{ fontSize: 13, margin: '4px 0 0', textAlign: 'center' }}>
              Ainda não tem conta?{' '}
              <a className="login__link" href="/request-invite">
                Criar conta
              </a>
            </p>
          </form>
        </Card>
      </section>
    </div>
  )
}
